import './App.css';
import { default as React } from 'react';
import { Entity, RELATION_OWNED_BY } from '@backstage/catalog-model';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogTable,
  CatalogTableColumnsFunc,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import {
  oktaAuthApiRef,
  configApiRef,
  useApi,
  useAnalytics,
} from '@backstage/core-plugin-api';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import { OAuthRequestDialog, SignInPage } from '@backstage/core-components';
import { ResilientAlertDisplay } from './components/ResilientAlertDisplay';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import {
  GitHubRepoSelectorExt,
  ADOProjectSelectorExt,
  GitHubRepoUrlInputExt,
  StorageSizeInputExt,
  ServiceAccountInfoExt,
  SecretsInputExt,
  SecretsValidationFieldExt,
  SecretsModePickerExt,
  DualEnvSecretsInputExt,
  DualEnvSecretsValidationFieldExt,
  PortFieldExt,
} from '@internal/frontend-plugin-microservices';
import { GithubTemplatePickerFieldExtension } from './scaffolder/GithubTemplatePickerExtension';
import { StandardTemplatePickerFieldExtension } from './scaffolder/StandardTemplatePickerExtension';
import { RepoNameFieldExtension } from './scaffolder/RepoNameFieldExtension';
import { CodeOwnersFieldExtension } from './scaffolder/CodeOwnersFieldExtension';
import { TeamPickerExtension } from './scaffolder/TeamPickerExtension';
import { CatalogTeamPickerExtension } from './scaffolder/CatalogTeamPickerExtension';
import { GithubSsoLoginExtension } from './scaffolder/GithubSsoLoginExtension';
import {
  LabelsFieldExtension,
  TopicsFieldExtension,
} from './scaffolder/LabelsFieldExtension';
import { RadioWidgetExtension } from './scaffolder/RadioWidgetExtension';
import { SolutionPickerExtension } from './scaffolder/SolutionPickerExtension';
import { DotnetApiOptionsExtension } from './scaffolder/DotnetApiOptionsExtension';
import { NodejsApiOptionsFieldExtension } from './scaffolder/NodejsApiOptionsExtension';
import { PythonServiceOptionsExtension } from './scaffolder/PythonServiceOptionsExtension';
import { AngularAppOptionsExtension } from './scaffolder/AngularAppOptionsExtension';
import { TerraformModuleOptionsExtension } from './scaffolder/TerraformModuleOptionsExtension';
import { TerraformInfraOptionsExtension } from './scaffolder/TerraformInfraOptionsExtension';
import { TerraformInfrastructureOptionsFieldExtension } from './scaffolder/TerraformInfrastructureOptionsExtension';
import { GcpProjectIdFieldExtension } from './scaffolder/GcpProjectIdFieldExtension';
import { CloudProjectPickerExtension } from './scaffolder/CloudProjectPickerExtension';
import { ProviderFieldExtension } from './scaffolder/ProviderFieldExtension';
import { ComponentTypeFieldExtension } from './scaffolder/ComponentTypeFieldExtension';
import { DescriptionFieldExtension } from './scaffolder/DescriptionFieldExtension';
import { AccessControlFieldExtension } from './scaffolder/AccessControlFieldExtension';
import { OwnerEmailsFieldExtension } from './scaffolder/OwnerEmailsFieldExtension';
import { CiamGroupPickerExtension } from './scaffolder/CiamGroupPickerExtension';
import { PublicAccessUserPickerExtension } from './scaffolder/PublicAccessUserPickerExtension';
import { StagingEnvironmentFieldExtension } from './scaffolder/StagingEnvironmentFieldExtension';
import { McpCallbackUrlDisplayExtension } from './scaffolder/McpCallbackUrlDisplayExtension';
import { AppNameFieldExtension } from './components/scaffolder/AppNameField';
import { AppUrlFieldExtension } from './components/scaffolder/AppUrlField';
import { CustomReviewStep } from './components/scaffolder/CustomReviewState';
import { CustomTaskPage } from './components/scaffolder/CustomTaskPage';
import { TaskOwnerFilterRestriction } from './components/scaffolder/TaskOwnerFilterRestriction';
import LightIcon from '@mui/icons-material/WbSunny';
import { UnifiedThemeProvider } from '@backstage/theme';
import { customTheme } from './themes/customTheme';
import { homePage } from './components/home/HomePage';
import { NewRelicPage } from '@backstage-community/plugin-newrelic';
import {
  RegularProjectsFilter,
  MicroappPage,
  createColumnsFunc,
  microappsColumnsFunc,
  AccessDeniedPage,
  AppUnavailablePage,
} from './components/catalog/microapps';
import { OnboardingTrackerPage } from './components/onboarding-tracker';
import { RepoLensPage } from '@internal/plugin-repo-lens-frontend';
import { PublicAppPage } from './components/catalog/public-apps/PublicAppPage';
import { McpServersPage } from './components/McpServers';

import {
  EntityKindPicker,
  EntityTypePicker,
  UserListPicker,
  EntityOwnerPicker,
  EntityNamespacePicker,
} from '@backstage/plugin-catalog-react';

import { CustomCatalogIndexPage } from './components/catalog/CustomCatalogIndexPage/CustomCatalog';
import { ExcludeRetiredFilter } from './components/catalog/ExcludeRetiredFilter';
import { BusinessUnitPicker } from './components/catalog/BusinessUnitFilter';
import { SolutionPicker } from './components/catalog/SolutionFilter';
import { isUnknownOwner } from './components/catalog/CatalogEntityCard';

const LEGACY_NAME_ANNOTATION = 'skystage.wellsky.com/legacy-name';

/** Replace the default Owner column render to hide "user:onspring/unknown". */
function hideUnknownOwnerColumn(columns: ReturnType<CatalogTableColumnsFunc>) {
  const ownerIdx = columns.findIndex(
    col => col.field === 'resolved.ownedByRelationsTitle',
  );
  if (ownerIdx >= 0) {
    const originalRender = columns[ownerIdx].render;
    columns[ownerIdx] = {
      ...columns[ownerIdx],
      render: (
        data: { entity: Entity; resolved: Record<string, unknown> },
        ...rest: unknown[]
      ) => {
        const ownerRef =
          data.entity?.relations?.find(
            (r: { type: string }) => r.type === RELATION_OWNED_BY,
          )?.targetRef || '';
        if (isUnknownOwner(ownerRef)) return null;
        return originalRender
          ? (originalRender as Function)(data, ...rest)
          : null;
      },
    };
  }
}

const solutionsColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const defaultColumns = CatalogTable.defaultColumnsFunc(entityListContext);

  // Insert Legacy Name column after the Description column
  const descIndex = defaultColumns.findIndex(
    col => col.title === 'Description',
  );
  const insertAt = descIndex >= 0 ? descIndex + 1 : defaultColumns.length - 1;

  const legacyNameColumn = {
    title: 'Legacy Name',
    field: 'entity.metadata.annotations.skystage.wellsky.com/legacy-name',
    searchable: true,
    customFilterAndSearch: (filter: string, data: { entity: Entity }) => {
      const legacyName =
        data.entity?.metadata?.annotations?.[LEGACY_NAME_ANNOTATION] || '';
      return legacyName.toLowerCase().includes(filter.toLowerCase());
    },
    render: (data: { entity: Entity }) => {
      return data.entity?.metadata?.annotations?.[LEGACY_NAME_ANNOTATION] || '';
    },
  };

  const columns = [...defaultColumns];
  columns.splice(insertAt, 0, legacyNameColumn);
  hideUnknownOwnerColumn(columns);
  return columns;
};

const businessUnitsColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const columns = [...CatalogTable.defaultColumnsFunc(entityListContext)];
  hideUnknownOwnerColumn(columns);
  return columns;
};

const app = createApp({
  apis,
  themes: [
    {
      id: 'custom-theme',
      title: 'My Custom Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={customTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);
      const analytics = useAnalytics();
      const disableAuth =
        config.getOptionalBoolean('app.disable_auth') || false;

      // Initialize redirecting as true to show loading screen immediately
      const [redirecting, setRedirecting] = React.useState(true);

      React.useEffect(() => {
        // Show loading screen briefly, then proceed to authentication
        const timer = setTimeout(() => {
          setRedirecting(false);
        }, 1000); // Show loading for 1 second

        return () => clearTimeout(timer);
      }, []);

      React.useEffect(() => {
        const trackLogin = async () => {
          analytics.captureEvent('sign_in', 'skystage-login', {
            attributes: {
              platform: config.getString('app.title'),
              timestamp: new Date().toISOString(),
            },
          });
        };

        if (!redirecting) {
          trackLogin();
        }
      }, [redirecting, analytics, config]);

      if (redirecting) {
        return (
          <div
            style={{
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              fontFamily: 'roboto, Helvetica, Arial, sans-serif',
              backgroundColor: '#f9f9f9',
            }}
          >
            <img
              src="skyStageLogo.svg"
              alt="SkyStage"
              style={{ height: '60px', marginBottom: '20px' }}
            />
            <div style={{ fontSize: '1.2rem', color: '#555' }}>Loading...</div>
          </div>
        );
      }

      // Redirect to guest provider if authentication is disabled
      if (disableAuth) {
        return <SignInPage {...props} auto providers={['guest']} />;
      }

      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'okta',
            title: 'Okta',
            message: 'Sign in using Okta',
            apiRef: oktaAuthApiRef,
          }}
        />
      );
    },
  },
});

// Create column functions for different pages
const myColumnsFunc = createColumnsFunc();

const routes = (
  <FlatRoutes>
    <Route path="/" element={homePage()} />
    {/* <Route path="/" element={<Navigate to="catalog" />} /> */}
    <Route
      path="/catalog"
      element={
        <CustomCatalogIndexPage
          key="catalog"
          title="Software Catalog"
          initialFilter="component"
          columns={myColumnsFunc}
          allowedKinds={[
            'component',
            'group',
            'location',
            'resource',
            'template',
            'user',
            'api',
          ]}
          filters={
            <>
              <EntityKindPicker
                initialFilter="component"
                allowedKinds={[
                  'component',
                  'group',
                  'location',
                  'resource',
                  'template',
                  'user',
                  'api',
                ]}
              />
              <EntityTypePicker />
              <BusinessUnitPicker />
              <SolutionPicker />
              <UserListPicker />
              <EntityOwnerPicker />
              <EntityNamespacePicker />
              <RegularProjectsFilter />
            </>
          }
        />
      }
    />
    <Route
      path="/solutions"
      element={
        <CustomCatalogIndexPage
          key="solutions"
          title="Solutions"
          initialFilter="system"
          allowedKinds={['System']}
          defaultViewMode="table"
          columns={solutionsColumnsFunc}
          filters={
            <>
              <EntityKindPicker
                initialFilter="system"
                allowedKinds={['System']}
              />
              <EntityTypePicker />
              <UserListPicker />
              <EntityOwnerPicker />
              <EntityNamespacePicker />
              <ExcludeRetiredFilter />
            </>
          }
        />
      }
    />

    <Route
      path="/business-units"
      element={
        <CustomCatalogIndexPage
          key="business-units"
          title="Business Units"
          initialFilter="domain"
          allowedKinds={['Domain']}
          defaultViewMode="table"
          columns={businessUnitsColumnsFunc}
        />
      }
    />

    <Route
      path="/apps"
      element={<MicroappPage columns={microappsColumnsFunc} />}
    />
    <Route path="/mcp-servers" element={<McpServersPage />} />
    <Route path="/public-apps" element={<PublicAppPage />} />
    <Route path="/onboarding-tracker" element={<OnboardingTrackerPage />} />
    <Route path="/repo-lens" element={<RepoLensPage />} />
    <Route path="/access-denied" element={<AccessDeniedPage />} />
    <Route path="/app-unavailable" element={<AppUnavailablePage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{ title: 'Create', subtitle: '' }}
          templateFilter={entity =>
            entity.metadata.annotations?.['skystage/hidden'] !== 'true'
          }
          components={{
            ReviewStepComponent: CustomReviewStep,
            TaskPageComponent: CustomTaskPage,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <GitHubRepoSelectorExt />
        <GithubSsoLoginExtension />
        <ADOProjectSelectorExt />
        <GitHubRepoUrlInputExt />
        <StorageSizeInputExt />
        <ServiceAccountInfoExt />
        <SecretsInputExt />
        <SecretsValidationFieldExt />
        <SecretsModePickerExt />
        <DualEnvSecretsInputExt />
        <DualEnvSecretsValidationFieldExt />
        <PortFieldExt />
        <AppNameFieldExtension />
        <AppUrlFieldExtension />
        <GithubTemplatePickerFieldExtension />
        <StandardTemplatePickerFieldExtension />
        <RepoNameFieldExtension />
        <CodeOwnersFieldExtension />
        <TeamPickerExtension />
        <CatalogTeamPickerExtension />
        <LabelsFieldExtension />
        <TopicsFieldExtension />
        <RadioWidgetExtension />
        <StagingEnvironmentFieldExtension />
        <AccessControlFieldExtension />
        <SolutionPickerExtension />
        <DotnetApiOptionsExtension />
        <NodejsApiOptionsFieldExtension />
        <PythonServiceOptionsExtension />
        <AngularAppOptionsExtension />
        <TerraformModuleOptionsExtension />
        <TerraformInfraOptionsExtension />
        <TerraformInfrastructureOptionsFieldExtension />
        <GcpProjectIdFieldExtension />
        <CloudProjectPickerExtension />
        <ProviderFieldExtension />
        <ComponentTypeFieldExtension />
        <DescriptionFieldExtension />
        <AccessControlFieldExtension />
        <CiamGroupPickerExtension />
        <PublicAccessUserPickerExtension />
        <OwnerEmailsFieldExtension />
        <McpCallbackUrlDisplayExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/newrelic" element={<NewRelicPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <ResilientAlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <TaskOwnerFilterRestriction />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
